import APPLICATION_STATUS from '@/constants/applicationStatus'
import APPLICATION_TYPE from '@/constants/applicationType'
import CORPORATION_SCOPE_TYPE from '@/constants/corporationScopeType'
import { DateTime } from 'luxon'

/**
 * Format application status into german human readable description
 * @param {string} status
 * @returns {string} Formatted application status to display
 */
const formatApplicationStatus = (status) => {
    switch (status) {
        case APPLICATION_STATUS.NOT_TRANSFERRED: return 'Angenommen (noch nicht weitergeleitet)'
        case APPLICATION_STATUS.TRANSFERRED: return 'Angenommen (weitergeleitet)'
        case APPLICATION_STATUS.WAITING_FOR_APPROVAL: return 'Freigabe erforderlich'
        case APPLICATION_STATUS.REJECTED: return 'Abgelehnt'
        case APPLICATION_STATUS.WAITING_FOR_PAYMENT: return 'Warte auf Zahlung'
        case APPLICATION_STATUS.ISSUED: return 'Ausgegeben'
        case APPLICATION_STATUS.PENDING: return 'Übernahme ausstehend'
        case APPLICATION_STATUS.ACCEPTED: return 'Übernommen'
        default: ''
    }
}

/**
 * Format application type into german human readable description
 * @param {string} status
 * @returns {string} Formatted application type to display
 */
const formatApplicationType = (applicationType) => {
    switch (applicationType) {
        case APPLICATION_TYPE.NEW_TICKET: return 'Neues Ticket'
        case APPLICATION_TYPE.UPDATE_CUSTOMER: return 'Änderung Kundendaten'
        case APPLICATION_TYPE.UPDATE_TICKETHOLDER: return 'Änderung Fahrkarteninhaber'
        case APPLICATION_TYPE.UPDATE_BANKACCOUNTHOLDER: return 'Änderung Kontodaten'
        case APPLICATION_TYPE.TICKET_EXCHANGE: return 'Tarifwechsel'
        case APPLICATION_TYPE.TICKET_REPLACEMENT: return 'Ersatzberechtigung'
        case APPLICATION_TYPE.TICKET_TERMINATION: return 'Kündigung Berechtigung'
        case APPLICATION_TYPE.GET_ACCESS: return 'Beantragung von Zugangsdaten'
        case APPLICATION_TYPE.ASSIGNMENT_USER: return 'Zuordnung eines Mitarbeiters'
        case APPLICATION_TYPE.DIRECT_SALE: return 'Direktverkauf'
        case APPLICATION_TYPE.DOCUMENT_TICKETHOLDER: return 'Dokument Fahrkarteninhaber'
        case APPLICATION_TYPE.TICKET_TERMINATION_ANONYMOUS: return 'Kündigungsanfrage'
        case APPLICATION_TYPE.TICKET_PROOF: return 'Nachweis Berechtigung'
        case APPLICATION_TYPE.TICKET_REFUND: return 'Erstattung'
        case APPLICATION_TYPE.TICKETHOLDER_SCHEDULED_SCHOOL_CHANGE: return 'Terminierte Schuländerung'
        default: ''
    }
}

/**
 * Format corporation scope into german human readable description
 * @param {string} corporationScope
 * @returns {string} Formatted corporation scope to display
 */
const formatCorporationScope = (corporationScope) => {
    switch (corporationScope) {
        case CORPORATION_SCOPE_TYPE.SCHOOLS: return 'Schulen'
        case CORPORATION_SCOPE_TYPE.SCHOOL_AUTHORITIES: return 'Schulträger'
    }
}

/**
 * Generates a filename.
 * This function is used to have a schema that all filenames in this document are similar
 * @param {string} label label indicating what this file contains
 * @param {string} corporationName corporation name to add to the filename
 * @param {string} fileEnding fileending including dot e.g. '.csv'
 * @returns {string} filename
 *
 * @example
 * const utility = new Utility()
 * utility.getFilename('ticket-holder', 'my-corporation-name', '.txt')
 */
const getFilename = (label, corporationName, fileEnding) => {
    const formattedDateTime = DateTime.now().toFormat('yyyyLLdd_HHmmss')
    return [ formattedDateTime, label, corporationName ].join('-') + fileEnding
}

/**
 * Returns a new array of school classes sorted by grade (ascending),
 * and then by class name alphabetically within the same grade.
 *
 * This avoids sorting like this: 1a, 1b, 10a, 10b, 2a, 2b ...
 *
 * The original array is not modified.
 * @param {object[]} unsortedSchoolClasses
 * @returns {object[]}
 */
const getSortedSchoolClasses = (unsortedSchoolClasses) => {
    return unsortedSchoolClasses.toSorted((a, b) => a.name.localeCompare(b.name)).sort( (a, b) => a.grade - b.grade)
}

/**
 * Sorts an array by a string property.
 * Returns an empty array if `items` is nullish.
 *
 * @param {object[] | null | undefined} items
 *   Object containing the array to sort.
 * @param {string} key
 *   The property name used for sorting.
 * @returns {object[]}
 */
export const sortByKey = (items, key) => {
    if (!items) {
        return []
    }
    return items.toSorted((a, b) => a[key].localeCompare(b[key]))
}

const Utility = {
    getFilename,
    formatCorporationScope,
    formatApplicationType,
    formatApplicationStatus,
    getSortedSchoolClasses,
    sortByKey
}

export {
    Utility
}